// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.

type FunctionType<TReturn> = (() => Promise<TReturn>) | (() => TReturn);
type FunctionTypeAny<TArgs extends any[], TReturn> = ((...args: TArgs) => Promise<TReturn>) | ((...args: TArgs) => TReturn);
type FunctionType1<T1, TReturn> = ((arg1: T1) => Promise<TReturn>) | ((arg1: T1) => TReturn);
type FunctionType2<T1, T2, TReturn> = ((arg1: T1, arg2: T2) => Promise<TReturn>) | ((arg1: T1, arg2: T2) => TReturn);
type FunctionType3<T1, T2, T3, TReturn> = ((arg1: T1, arg2: T2, arg3: T3) => Promise<TReturn>) | ((arg1: T1, arg2: T2, arg3: T3) => TReturn);
type FunctionType4<T1, T2, T3, T4, TReturn> =
    ((arg1: T1, arg2: T2, arg3: T3, arg4: T4) => Promise<TReturn>)
    | ((arg1: T1, arg2: T2, arg3: T3, arg4: T4) => TReturn);
type FunctionType5<T1, T2, T3, T4, T5, TReturn> =
    ((arg1: T1, arg2: T2, arg3: T3, arg4: T4, arg5: T5) => Promise<TReturn>)
    | ((arg1: T1, arg2: T2, arg3: T3, arg4: T4, arg5: T5) => TReturn);

/**
 * Limits the execution of the provided function to once within the specified wait time
 *
 * @param func Function to debounce
 * @param wait Time to wait in milliseconds between calls
 * @param immediate Optional paramater. If True - call the inner function immediately and do not make subsequent calls till after the specified wait
 * @returns a Promise that resolves when the Function is called
 */
export function debounce<TReturn>(
    func: FunctionType<TReturn>,
    wait: number,
    immediate?: boolean
): () => Promise<TReturn>;

/**
 * Limits the execution of the provided function to once within the specified wait time
 *
 * @param func Function to debounce
 * @param wait Time to wait in milliseconds between calls
 * @param immediate Optional paramater. If True - call the inner function immediately and do not make subsequent calls till after the specified wait
 * @returns a Promise that resolves when the Function is called
 */
export function debounce<T1, TReturn>(
    func: FunctionType1<T1, TReturn>,
    wait: number,
    immediate?: boolean
): (arg1: T1) => Promise<TReturn>;

/**
 * Limits the execution of the provided function to once within the specified wait time
 *
 * @param func Function to debounce
 * @param wait Time to wait in milliseconds between calls
 * @param immediate Optional paramater. If True - call the inner function immediately and do not make subsequent calls till after the specified wait
 * @returns a Promise that resolves when the Function is called
 */
export function debounce<T1, T2, TReturn>(
    func: FunctionType2<T1, T2, TReturn>,
    wait: number,
    immediate?: boolean
): (arg1: T1, arg2: T2) => Promise<TReturn>;

/**
 * Limits the execution of the provided function to once within the specified wait time
 *
 * @param func Function to debounce
 * @param wait Time to wait in milliseconds between calls
 * @param immediate Optional paramater. If True - call the inner function immediately and do not make subsequent calls till after the specified wait
 * @returns a Promise that resolves when the Function is called
 */
export function debounce<T1, T2, T3, TReturn>(
    func: FunctionType3<T1, T2, T3, TReturn>,
    wait: number,
    immediate?: boolean
): (arg1: T1, arg2: T2, arg3: T3) => Promise<TReturn>;

/**
 * Limits the execution of the provided function to once within the specified wait time
 *
 * @param func Function to debounce
 * @param wait Time to wait in milliseconds between calls
 * @param immediate Optional paramater. If True - call the inner function immediately and do not make subsequent calls till after the specified wait
 * @returns a Promise that resolves when the Function is called
 */
export function debounce<T1, T2, T3, T4, TReturn>(
    func: FunctionType4<T1, T2, T3, T4, TReturn>,
    wait: number,
    immediate?: boolean
): (arg1: T1, arg2: T2, arg3: T3, arg4: T4) => Promise<TReturn>;

/**
 * Limits the execution of the provided function to once within the specified wait time
 *
 * @param func Function to debounce
 * @param wait Time to wait in milliseconds between calls
 * @param immediate Optional paramater. If True - call the inner function immediately and do not make subsequent calls till after the specified wait
 * @returns a Promise that resolves when the Function is called
 */
export function debounce<T1, T2, T3, T4, T5, TReturn>(
    func: FunctionType5<T1, T2, T3, T4, T5, TReturn>,
    wait: number,
    immediate?: boolean
): (arg1: T1, arg2: T2, arg3: T3, arg4: T4, arg5: T5) => Promise<TReturn>;

/**
 * Limits the execution of the provided function to once within the specified wait time
 *
 * @param func Function to debounce
 * @param wait Time to wait in milliseconds between calls
 * @param immediate Optional paramater. If True - call the inner function immediately and do not make subsequent calls till after the specified wait
 * @returns a Promise that resolves when the Function is called
 */
export function debounce<TArgs extends any[], TReturn>(
    func: FunctionTypeAny<TArgs, TReturn>,
    wait: number,
    immediate?: boolean
): (...args: any[]) => Promise<TReturn> {

    let timer: NodeJS.Timeout;
    return function(this: any, ...args: any[]) {
        const context = this;

        return new Promise<TReturn>(resolve => {
            const later = () => {
                timer = null;
                if (!immediate) {
                    resolve(func.apply(context, args));
                }
            };

            const callNow = immediate && !timer;
            clearTimeout(timer);
            timer = setTimeout(later, wait);
            if (callNow) {
                resolve(func.apply(context, args));
            }
        });
    };
}
